import { useEffect, useRef, useState } from 'react'
import prefersReducedMotion from '@/shared/tools/prefersReducedMotion'
import usePulseScope from './usePulseScope'
import { PULSE_COLLAPSE_MS } from './styled-components'

type Phase = 'closed' | 'mounting' | 'expanding' | 'open' | 'closing'

const usePulseCollapse = (open: boolean, scrollOnOpen = true) => {
  const isPulse = usePulseScope()
  const frameRef = useRef<HTMLDivElement>(null)
  const [phase, setPhase] = useState<Phase>(open ? 'open' : 'closed')

  useEffect(() => {
    if (open) {
      setPhase(current => {
        if (current !== 'closed' && current !== 'closing') return current
        return prefersReducedMotion() ? 'open' : 'mounting'
      })
      return
    }
    setPhase(current => {
      if (current === 'closed') return current
      return prefersReducedMotion() ? 'closed' : 'closing'
    })
  }, [open])

  useEffect(() => {
    if (phase === 'mounting') {
      let inner = 0
      const outer = requestAnimationFrame(() => {
        inner = requestAnimationFrame(() => setPhase('expanding'))
      })
      return () => {
        cancelAnimationFrame(outer)
        cancelAnimationFrame(inner)
      }
    }
    if (phase === 'expanding') {
      const timer = window.setTimeout(() => {
        setPhase('open')
        if (scrollOnOpen) frameRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
      }, PULSE_COLLAPSE_MS)
      return () => window.clearTimeout(timer)
    }
    if (phase === 'closing') {
      const timer = window.setTimeout(() => setPhase('closed'), PULSE_COLLAPSE_MS)
      return () => window.clearTimeout(timer)
    }
    return undefined
  }, [phase, scrollOnOpen])

  const settled = phase === 'open'

  return {
    isPulse,
    frameRef,
    rendered: phase !== 'closed',
    expanded: phase === 'expanding' || settled,
    settled,
  }
}

export default usePulseCollapse
